import { useQueryClient } from '@tanstack/react-query';
import { useCallback, useState } from 'react';
import { updateTestRun } from '@/features/executions/data/testRuns';
import { buildTestRunParentInvalidations } from '@/features/executions/queries/executionInvalidationFilters';
import { useOrganisationFilter } from '@/features/organisations/providers/organisationHooks';
import { useDetailEditEditor } from '@/features/shared/hooks/useDetailEditEditor';
import { patchQueryResultCaches } from '@/shared/data/queryResultCache';
import {
  gridPaths,
  gridQueryKey,
  invalidateGridQuery,
  invalidateFeatures,
  invalidateReleases,
  invalidateSuites,
  invalidateTests,
  invalidateTestRunDetail,
  invalidateTestRuns,
  queryKeys,
  useInvalidateQueries,
} from '@/shared/hooks';
import type { TagColor } from '@/shared/data/tags';
import type { TestRunDto } from '@flowqa/shared';

type TestRunEditSaveInput = {
  colors: TagColor[];
  durationMs?: number | null;
  status?: TestRunDto['status'];
  tags: string[];
  timestamp?: Date | null;
};

type TestRunEditDialogState = {
  editLoading: boolean;
  editTarget: TestRunDto | null;
  onEditClose: () => void;
  onEditRow: (row: TestRunDto) => void;
  onEditSave: (input: TestRunEditSaveInput) => Promise<void>;
};

export const useTestRunEditDialog = (selectedProjectId: string | null): TestRunEditDialogState => {
  const queryClient = useQueryClient();
  const invalidateQueries = useInvalidateQueries();
  const { selectedOrganisationId } = useOrganisationFilter();
  const [editTarget, setEditTarget] = useState<TestRunDto | null>(null);

  const editEditor = useDetailEditEditor<TestRunDto['status']>({
    projectId: editTarget?.projectId ?? null,
    successMessage: 'Test run updated',
    saveEntity: async ({ tags, status, timestamp, durationMs }) => {
      if (!editTarget) return;
      const organisationId = editTarget.organisationId ?? selectedOrganisationId ?? null;
      const updated = await updateTestRun(
        { id: editTarget.id, tags, status, timestamp, durationMs },
        organisationId,
      );
      patchQueryResultCaches(
        queryClient,
        [
          { queryKey: queryKeys.testRuns(selectedProjectId, organisationId) },
          { queryKey: gridQueryKey(gridPaths.testRuns) },
        ],
        updated,
      );
      await invalidateQueries([
        ...buildTestRunParentInvalidations(editTarget),
        invalidateTestRuns(selectedProjectId, organisationId),
        invalidateTestRunDetail(editTarget.id, organisationId),
        invalidateTests(selectedProjectId, organisationId),
        invalidateSuites(selectedProjectId, organisationId),
        invalidateFeatures(selectedProjectId, organisationId),
        invalidateReleases(selectedProjectId, organisationId),
        invalidateGridQuery(gridPaths.testRuns),
        invalidateGridQuery(gridPaths.tests),
        invalidateGridQuery(gridPaths.suites),
        invalidateGridQuery(gridPaths.features),
        invalidateGridQuery(gridPaths.releases),
      ]);
    },
  });

  const onEditClose = useCallback((): void => {
    if (editEditor.isPending) return;
    setEditTarget(null);
  }, [editEditor.isPending]);

  const onEditSave = useCallback(
    async ({
      tags,
      colors,
      status,
      timestamp,
      durationMs,
    }: TestRunEditSaveInput): Promise<void> => {
      await editEditor.save({ tags, colors, status, timestamp, durationMs });
      setEditTarget(null);
    },
    [editEditor],
  );

  return {
    editLoading: editEditor.isPending,
    editTarget,
    onEditClose,
    onEditRow: setEditTarget,
    onEditSave,
  };
};
